import { useEffect, useMemo } from "react";

import { Divider, Typography } from "antd";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";

import {
  useGetQuestionByIdQuery,
  useLazyGetAnswerByIdQuery,
} from "@/redux/service.js";
import { OptionType } from "@/shared/utils/constants.js";
import DataBlockContainer from "../../../datablocks/details/index.tsx";
import CheckboxIcon from "@/assets/icons/checkbox.svg?react";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer.tsx";

import "../styles.css";

const { Text } = Typography;

const Answer = ({ option, answerId }) => {
  const { t } = useTranslation();

  const [getAnswerById, answerResponse] = useLazyGetAnswerByIdQuery();

  useEffect(() => {
    if (answerId) {
      getAnswerById({
        id: answerId,
      });
    }
  }, [answerId]);

  const children = useMemo(() => {
    if (answerResponse.isSuccess) {
      if (
        [
          OptionType.SINGLE_CHOICE.value,
          OptionType.MULTIPLE_CHOICE.value,
        ].includes(option.type)
      ) {
        return (
          <div style={{ display: "flex" }}>
            <BaseSvgRenderer component={CheckboxIcon} />
            <div className="ms-3">
              <Text style={{ color: "black", fontSize: "17px" }}>
                {Array.isArray(answerResponse.data.textAnswer) &&
                answerResponse.data.textAnswer.every((i) => i != null)
                  ? `${option.label} - ${option.relatedOptions.find((opt) => opt.id === answerResponse.data.textAnswer[0])?.label}`
                  : option.label}
              </Text>
            </div>
          </div>
        );
      }

      if (OptionType.CHOICE_TEXT.value === option.type) {
        return (
          <div style={{ display: "flex" }}>
            <BaseSvgRenderer component={CheckboxIcon} />
            <div className="ms-3">
              <Text style={{ color: "black", fontSize: "17px" }}>
                {t("other")}
              </Text>
              {Array.isArray(answerResponse.data.textAnswer) && (
                <Text>
                  :&nbsp;
                  {answerResponse.data.textAnswer.map((item) => {
                    if (item?.choice) {
                      return `${item.choice} - ${option.relatedOptions.find((opt) => opt.id === item.scale)?.label}`;
                    }
                    return item;
                  })}
                </Text>
              )}
            </div>
          </div>
        );
      }

      if (
        [OptionType.TEXT.value, OptionType.NUMBER.value].includes(option.type)
      ) {
        return (
          <Text style={{ color: "black" }}>
            {answerResponse.data.textAnswer}
          </Text>
        );
      }
    }
  }, [answerResponse, option]);

  return (
    answerResponse.isSuccess && (
      <div>
        {children}
        {answerResponse.data.attachedBlocks.map((block) => (
          <DataBlockContainer
            key={block.id}
            initialDataBlock={block}
            showCtas={false}
          />
        ))}
        {answerResponse.data.attachedBlocks.length === 0 && <Divider />}
      </div>
    )
  );
};

Answer.propTypes = {
  answerId: PropTypes.number.isRequired,
  option: PropTypes.object.isRequired,
};

export const Question = ({ questionId, questionIndex }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { data: question, isSuccess } = useGetQuestionByIdQuery({
    questionId,
    filters: {
      organization: searchParams.get("organization"),
      user: null,
    },
  });

  const answeredOptions = useMemo(() => {
    return question
      ? question.options.filter((option) => option.answer != null)
      : [];
  }, [question]);

  const answersIncludedInView = useMemo(() => {
    return answeredOptions.map((option) => option.answer);
  }, [answeredOptions]);

  return (
    isSuccess && (
      <div key={question.id} className="question-answered">
        <h6 className="header-s-medium">
          {questionIndex + 1 + "."} {question.label}
        </h6>

        <div className="">
          {answeredOptions.length === 0 ? (
            <div>
              <Text>{t("noAnswerForQuestion")}</Text>
            </div>
          ) : (
            answersIncludedInView &&
            answeredOptions
              .filter((option) => answersIncludedInView.includes(option.answer))
              .map((option) => (
                <Answer
                  key={option.id}
                  answerId={option.answer}
                  option={option}
                />
              ))
          )}
        </div>
      </div>
    )
  );
};

Question.propTypes = {
  questionId: PropTypes.number.isRequired,
  questionIndex: PropTypes.number.isRequired,
};
